import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token") || "";
    const [state, setState] = useState("verifying"); // verifying | ok | error
    const [message, setMessage] = useState("");
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;
        if (!token) {
            setState("error");
            setMessage("This verification link is missing its token.");
            return;
        }
        (async () => {
            try {
                const { data } = await client.post("/auth/verify-email", { token });
                setState("ok");
                setMessage(data.already_verified
                    ? "Your email was already verified. All set."
                    : "Email confirmed. You can now spend credits and run every audit.");
            } catch (err) {
                setState("error");
                setMessage(err?.response?.data?.detail || "Couldn't verify this link. Request a new one from Settings.");
            }
        })();
    }, [token]);

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-background">
            <div className="w-full max-w-sm" data-testid="verify-email-page">
                <Link to="/" data-testid="brand-link" className="inline-flex items-center gap-2 mb-10">
                    <Logo className="h-14 w-auto" />
                </Link>

                {state === "verifying" && (
                    <div data-testid="verify-loading">
                        <div className="overline mb-3">// checking</div>
                        <h1 className="font-display text-3xl tracking-tight mb-4">Confirming your email…</h1>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                            <Loader2 className="w-4 h-4 animate-spin" /> verifying token
                        </div>
                    </div>
                )}

                {state === "ok" && (
                    <div data-testid="verify-success">
                        <div className="overline mb-3 flex items-center gap-2 text-success">
                            <CheckCircle2 className="w-3 h-3" /> verified
                        </div>
                        <h1 className="font-display text-3xl tracking-tight mb-3">You're in.</h1>
                        <p className="text-sm text-muted-foreground mb-8">{message}</p>
                        <button
                            data-testid="verify-continue-btn"
                            onClick={() => nav("/app", { replace: true })}
                            className="w-full bg-primary text-primary-foreground py-3 font-medium hover:bg-primary/90"
                        >
                            Continue to InPhase →
                        </button>
                    </div>
                )}

                {state === "error" && (
                    <div data-testid="verify-error">
                        <div className="overline mb-3 flex items-center gap-2 text-destructive">
                            <XCircle className="w-3 h-3" /> couldn't verify
                        </div>
                        <h1 className="font-display text-3xl tracking-tight mb-3">Link didn't work.</h1>
                        <p className="text-sm text-muted-foreground mb-8">{message}</p>
                        <Link
                            to="/app/settings"
                            data-testid="verify-resend-link"
                            className="text-primary hover:underline text-sm"
                        >
                            → Request a new link from Settings
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
